import React, { useState, useEffect } from "react";
import { Form, Button, Table, Spinner, Alert, Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import API from "../../services/api";

const PaymentHistory = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTableNumber = queryParams.get("table") || "";

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [tableNumber, setTableNumber] = useState(initialTableNumber);
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [error, setError] = useState(null);

    // Edit payment states
    const [editingPayment, setEditingPayment] = useState(null);
    const [editForm, setEditForm] = useState({
        total_amount: "",
        discount: "",
        paid_amount: "",
        payment_method: "",
    });
    const [saving, setSaving] = useState(false);

    const summary = payments.reduce(
        (acc, p) => {
            acc.total += Number(p.total_amount);
            acc.discount += Number(p.discount);
            acc.paid += Number(p.paid_amount);
            return acc;
        },
        { total: 0, discount: 0, paid: 0 }
    );

    /** ---- Fetch Payments ---- **/
    const fetchPayments = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { from_date: fromDate, to_date: toDate };
            if (tableNumber) params.table_number = tableNumber;
            const res = await API.get("/payment-history", { params });
            setPayments(res.data.payments || []);
        } catch (err) {
            console.error(err);
            setError("Failed to load payment history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments();
        // eslint-disable-next-line
    }, [tableNumber]);

    /** ---- Export CSV ---- **/
    const handleExportCSV = () => {
        if (!payments.length) return;

        const paymentMethods = ["cash", "qr", "card"];
        const calcGroup = (arr) => {
            const totals = {
                total_bill: arr.reduce((sum, p) => sum + Number(p.total_amount), 0),
                total: arr.reduce((sum, p) => sum + Number(p.paid_amount), 0),
                discount: arr.reduce((sum, p) => sum + Number(p.discount), 0),
                count: arr.length,
                cash: 0,
                qr: 0,
                card: 0
            };
            arr.forEach(p => {
                if (paymentMethods.includes(p.payment_method)) {
                    totals[p.payment_method] += Number(p.paid_amount);
                }
            });
            return totals;
        };

        const takeout = payments.filter(p => p.table_number === 20);
        const dinein = payments.filter(p => p.table_number !== 20);
        const takeoutTotals = calcGroup(takeout);
        const dineinTotals = calcGroup(dinein);
        const grandTotals = {
            total_bill: takeoutTotals.total_bill + dineinTotals.total_bill,
            total: takeoutTotals.total + dineinTotals.total,
            discount: takeoutTotals.discount + dineinTotals.discount,
            count: takeoutTotals.count + dineinTotals.count,
            cash: takeoutTotals.cash + dineinTotals.cash,
            qr: takeoutTotals.qr + dineinTotals.qr,
            card: takeoutTotals.card + dineinTotals.card
        };

        const rows = [
            {
                Label: "Takeout",
                "Total Sale Quantity": takeoutTotals.count,
                "Total Cash Sale": takeoutTotals.cash.toFixed(0),
                "Total Cr. Card Sale": takeoutTotals.card.toFixed(0),
                "Total Qr Pay Sale": takeoutTotals.qr.toFixed(0),
                "Total Sale(without discount)": takeoutTotals.total_bill.toFixed(0),
                "Total Sale": takeoutTotals.total.toFixed(0),
                "Total Discount amount": takeoutTotals.discount.toFixed(0)
            },
            {
                Label: "Dine-In",
                "Total Sale Quantity": dineinTotals.count,
                "Total Cash Sale": dineinTotals.cash.toFixed(0),
                "Total Cr. Card Sale": dineinTotals.card.toFixed(0),
                "Total Qr Pay Sale": dineinTotals.qr.toFixed(0),
                "Total Sale(without discount)": dineinTotals.total_bill.toFixed(0),
                "Total Sale": dineinTotals.total.toFixed(0),
                "Total Discount amount": dineinTotals.discount.toFixed(0)
            },
            {
                Label: "Grand Total",
                "Total Sale Quantity": grandTotals.count,
                "Total Cash Sale": grandTotals.cash.toFixed(0),
                "Total Cr. Card Sale": grandTotals.card.toFixed(0),
                "Total Qr Pay Sale": grandTotals.qr.toFixed(0),
                "Total Sale(without discount)": grandTotals.total_bill.toFixed(0),
                "Total Sale": grandTotals.total.toFixed(0),
                "Total Discount amount": grandTotals.discount.toFixed(0)
            }
        ];

        const headers = Object.keys(rows[0]).join(",");
        const values = rows.map(r => Object.values(r).join(",")).join("\n");
        const csvContent = `${headers}\n${values}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `payment_summary_${fromDate}_${toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /** ---- Edit Payment ---- **/
    const handleEditPayment = (payment) => {
        setEditingPayment(payment);
        setEditForm({
            total_amount: payment.total_amount,
            discount: payment.discount,
            paid_amount: payment.paid_amount,
            payment_method: payment.payment_method,
        });
    };

    const handleSaveEdit = async () => {
        if (!editingPayment) return;
        setSaving(true);

        try {
            const payload = {
                id: editingPayment.id,
                total_amount: editForm.total_amount,
                discount: editForm.discount,
                paid_amount: editForm.paid_amount,
                payment_method: editForm.payment_method,
                card_number: editForm.payment_method === "card" ? editForm.card_number : null,
            };

            await API.post("/edit-payment", payload);

            setEditingPayment(null);
            fetchPayments();
        } catch (err) {
            console.error("Failed to update payment", err);
            alert("Failed to save changes!");
        } finally {
            setSaving(false);
        }
    };
    /** ---- Print Summary for 80mm Thermal Printer ---- **/
    const handlePrintSummary = () => {
        if (!payments.length) return;

        // Calculate grouped totals
        const paymentMethods = ["cash", "qr", "card"];
        const calcGroup = (arr) => {
            const totals = {
                total_bill: arr.reduce((sum, p) => sum + Number(p.total_amount), 0),
                total: arr.reduce((sum, p) => sum + Number(p.paid_amount), 0),
                discount: arr.reduce((sum, p) => sum + Number(p.discount), 0),
                count: arr.length,
                cash: 0,
                qr: 0,
                card: 0
            };
            arr.forEach(p => {
                if (paymentMethods.includes(p.payment_method)) {
                    totals[p.payment_method] += Number(p.paid_amount);
                }
            });
            return totals;
        };

        const takeout = payments.filter(p => p.table_number === 20);
        const dinein = payments.filter(p => p.table_number !== 20);
        const takeoutTotals = calcGroup(takeout);
        const dineinTotals = calcGroup(dinein);
        const grandTotals = {
            total_bill: takeoutTotals.total_bill + dineinTotals.total_bill,
            total: takeoutTotals.total + dineinTotals.total,
            discount: takeoutTotals.discount + dineinTotals.discount,
            count: takeoutTotals.count + dineinTotals.count,
            cash: takeoutTotals.cash + dineinTotals.cash,
            qr: takeoutTotals.qr + dineinTotals.qr,
            card: takeoutTotals.card + dineinTotals.card,
            tax: takeoutTotals.total + dineinTotals.total - Number((takeoutTotals.total * 100) / 110 + (dineinTotals.total * 100) / 108).toFixed(0)
        };

        // Create HTML for printing on 80mm thermal printer
        const printHTML = `
    <html>
        <head>
            <title>${new Date().toLocaleDateString()} Payment Summary</title>
            <style>
              @page { size: 80mm auto; margin: 0; }
                body {
                    font-family: "Courier New", monospace;
                    width: 80mm;
                    padding: 5mm;
                }
                .logo {
                    text-align: center;
                    margin-bottom: 4px;
                }
                .logo img {
                    width: 120px;
                    /*height: 80px;*/
                }
                .date {
                    text-align: right;
                    margin-bottom: 8px;
                }
                .row {
                    display: flex;
                    justify-content: space-between;
                    margin: 2px 0;
                }
                .separator {
                    border-bottom: 1px dashed #000;
                    margin: 4px 0;
                }
                h2 {
                    text-align: center;
                    margin: 4px 0;
                }
            </style>
        </head>
        <body>
            <div class="logo">
                <img src="https://zamzam.jp/logo.png" alt="https://zamzam.jp/logo.png" />
            </div>
            
            <div class="date">${new Date().toLocaleDateString()}</div>
            <div class="separator"></div>

            <div class="row"><span>Total Sale</span><span>${grandTotals.total_bill - grandTotals.discount}</span></div>
            <div class="row"><span>Total Sale (Withour Discount)</span><span>${grandTotals.total_bill}</span></div>
            <div class="row"><span>Tax Amount</span><span>${grandTotals.tax}</span></div>
            <div class="row"><span>Sale(After Tax)</span><span>${Number(grandTotals.total_bill - grandTotals.tax).toFixed(0)}</span></div>
            <div class="row"><span>Takeout Count</span><span>${takeoutTotals.count}</span></div>
            <div class="row"><span>Dine-in Count</span><span>${dineinTotals.count}</span></div>
            <div class="row"><span>Total Count</span><span>${grandTotals.count}</span></div>
            <div class="row"><span>Cash Sale</span><span>${grandTotals.cash}</span></div>
            <div class="row"><span>Credit Card Sale</span><span>${grandTotals.card}</span></div>
            <div class="row"><span>Qr Pay Sale</span><span>${grandTotals.qr}</span></div>
        </body>
    </html>
    `;

        const printWindow = window.open("", "", "width=300,height=600");
        printWindow.document.write(printHTML);
        printWindow.document.close();

// Wait until the printWindow DOM is fully loaded
        printWindow.onload = () => {
            const logo = printWindow.document.getElementById("logo");

            if (!logo) {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
                return;
            }

            logo.onload = () => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            };

            // Fallback if image is cached and already loaded
            if (logo.complete) {
                logo.onload();
            }
        };


    };



    /** ---- Form submit ---- **/
    const handleSubmit = (e) => {
        e.preventDefault();
        fetchPayments();
    };

    return (
        <div className="p-4">
            <h2>💰 Payment History</h2>

            {/* Filter Form */}
            <Form onSubmit={handleSubmit} className="mb-4">
                <div className="d-flex gap-3 flex-wrap align-items-end">
                    <Form.Group>
                        <Form.Label>Table Number</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter table number"
                            value={tableNumber}
                            onChange={(e) => setTableNumber(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Filter"}
                        </Button>
                    </Form.Group>
                </div>
            </Form>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-end mb-2">
                <Button
                    variant="info"
                    onClick={handlePrintSummary}
                    disabled={!payments.length}
                >
                    🖨️ Print Summary
                </Button>
                <Button
                    variant="success"
                    onClick={handleExportCSV}
                    disabled={!payments.length}
                >
                    📤 Export CSV
                </Button>
            </div>

            {/* Payments Table */}
            <Table striped bordered hover responsive size="sm">
                <thead>
                <tr>
                    <th>ID</th>
                    <th>Table</th>
                    <th>Total</th>
                    <th>Discount</th>
                    <th>Paid</th>
                    <th>Method</th>
                    <th>Card Number</th>
                    <th>Created</th>
                    <th>Actions</th>
                </tr>
                </thead>

                <tbody>
                {payments.length > 0 ? (
                    payments.map((p) => (
                        <tr key={p.id}>
                            <td>{p.id}</td>
                            <td>{p.table_number}</td>
                            <td>¥{Number(p.total_amount).toFixed(0)}</td>
                            <td>¥{Number(p.discount).toFixed(0)}</td>
                            <td>¥{Number(p.paid_amount).toFixed(0)}</td>
                            <td>{p.payment_method}</td>
                            <td>{p.card_number || "-"}</td>
                            <td>{new Date(p.created_at).toLocaleString()}</td>
                            <td>
                                <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={() => handleEditPayment(p)}
                                >
                                    ✏️ Edit
                                </Button>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="9" className="text-center">
                            {loading ? "Loading..." : "No payments found"}
                        </td>
                    </tr>
                )}
                </tbody>

                {payments.length > 0 && (
                    <tfoot>
                    <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                        <td colSpan="2" className="text-end">Total:</td>
                        <td>¥{summary.total.toFixed(0)}</td>
                        <td>¥{summary.discount.toFixed(0)}</td>
                        <td>¥{summary.paid.toFixed(0)}</td>
                        <td colSpan="4"></td>
                    </tr>
                    </tfoot>
                )}
            </Table>

            {/* Edit Modal */}
            <Modal show={!!editingPayment} onHide={() => setEditingPayment(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Payment #{editingPayment?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>

                        {/* Total Amount */}
                        <Form.Group className="mb-2">
                            <Form.Label>Total Amount</Form.Label>
                            <Form.Control
                                type="number"
                                value={editForm.total_amount}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, total_amount: e.target.value })
                                }
                            />
                        </Form.Group>

                        {/* Discount */}
                        <Form.Group className="mb-2">
                            <Form.Label>Discount</Form.Label>
                            <Form.Control
                                type="number"
                                value={editForm.discount}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, discount: e.target.value })
                                }
                            />
                        </Form.Group>

                        {/* Paid Amount */}
                        <Form.Group className="mb-2">
                            <Form.Label>Paid Amount</Form.Label>
                            <Form.Control
                                type="number"
                                value={editForm.paid_amount}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, paid_amount: e.target.value })
                                }
                            />
                        </Form.Group>

                        {/* Payment Method */}
                        <Form.Group className="mb-2">
                            <Form.Label>Payment Method</Form.Label>
                            <Form.Select
                                value={editForm.payment_method}
                                onChange={(e) =>
                                    setEditForm({
                                        ...editForm,
                                        payment_method: e.target.value,
                                        // Reset card fields when switching away
                                        card_number:
                                            e.target.value !== "card" ? "" : editForm.card_number,
                                    })
                                }
                            >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="qr">QR</option>
                            </Form.Select>
                        </Form.Group>

                        {/* Card Number (visible only for card payments) */}
                        {editForm.payment_method === "card" && (
                            <Form.Group className="mb-2">
                                <Form.Label>Card Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter card number"
                                    value={editForm.card_number}
                                    onChange={(e) =>
                                        setEditForm({ ...editForm, card_number: e.target.value })
                                    }
                                />
                            </Form.Group>
                        )}

                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditingPayment(null)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default PaymentHistory;
